/**
 * LeatherDeleteController — the Delete sub-tool for leather objects (#1000,
 * epic #781): point at a part, cutout or seam and click to remove it.
 *
 * A tool OWNS a controller instance and DRIVES it by composition, the
 * arrangement `LeatherRoundController` / `LeatherMirrorController` /
 * `DeleteSliceController` already use.
 *
 * WHAT THIS ADDS, AND WHAT IT DELIBERATELY DOESN'T
 *
 * Leather already had two ways to delete: the Delete / Backspace key and the
 * Parts-panel trashcan, both routed through `deleteSelection` (#474). Both need
 * you to SELECT first. What was missing is construction's Delete sub-tool
 * gesture — point at a thing, click, it's gone — which is far quicker when
 * clearing several objects, and that is what this adds. The commit itself still
 * goes through `deleteSelection`, so cascade (a part takes its cutouts, seams,
 * holes and hardware with it), constraint pruning and undo behave identically
 * however the delete was triggered.
 *
 * DELETING A POINT stays with the Edit-points method, which already owns it
 * (#969 / #970) including the refusal below a floor of 3 points on a closed
 * outline / 2 on a seam, and the anchor lock + bezier-memory remapping that
 * has to travel with a removed anchor. Duplicating that here would fork it for
 * no user gain — the action is one click away in Edit points.
 *
 * DELETING ONE RUN OF A SEAM does live here (#1030), the leather answer to
 * construction's segment delete. Hovering a seam highlights just the stretch
 * under the cursor and a tap takes that stretch, leaving the rest; Shift takes
 * the whole object, the same modifier construction's Delete uses (#327). A part
 * outline or cut-out edge still comes off whole — an open part outline is not a
 * part — and so does a seam that is DERIVED (traced from construction lines, or
 * cropped to its part), since cutting a run of one would be undone by the next
 * commit's re-derive. The highlight always traces exactly what the tap removes,
 * so which of those a seam is never has to be guessed.
 */
import type paper from "paper";
import type { Scene } from "../canvas/Scene";
import {
  isVisible,
  nextStitchName,
  pieceStitchLines,
  type Project,
  type StitchLine,
} from "../model/Project";
import { newId } from "../model/ids";
import { pieceDisplayOffset, translatePathData } from "../model/pieceTransform";
import {
  pickLeatherObjectAt,
  selectionForPick,
  type LeatherPick,
} from "./leatherPick";
import { seamPathData } from "../model/seamGeometry";
import {
  nearestSeamRun,
  seamsAfterRunDelete,
} from "../model/seamSegmentDelete";
import { cssVar } from "../colors";

/** Highlight stroke for whatever the next click would remove. Read through
 *  functions so a theme switch is picked up. */
const ACCENT = () => cssVar("--canvas-accent");
const ACCENT_OVER_LEATHER = () => cssVar("--canvas-accent-leather");
/** The red construction's Delete sub-tool paints a doomed stretch. */
const DELETE_HOVER = () => cssVar("--construction-broken");

export interface LeatherDeleteHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  setSelection(sel: Parameters<Scene["setSelection"]>[0]): void;
  /** Remove whatever is selected — the shared path the Delete key and the
   *  Parts-panel trashcan use, so every route deletes identically. */
  deleteSelection(): void;
  /** Commit a model edit. Only the seam-run cut needs it: every whole-object
   *  delete still goes through `deleteSelection`. */
  commit(label: string, mutate: (proj: Project) => Project): void;
}

/** How close (mm) the cursor has to be to a seam run to target it. Matches the
 *  grace `pickLeatherObjectAt` already gives a seam — a run the pick resolved
 *  to must always resolve to one of that seam's runs, never to nothing. */
const RUN_PICK_TOL_MM = 3;

/** A seam stripped of everything a destructive reshape invalidates.
 *
 *  Two groups, both of which go quietly wrong if left behind. `smoothAnchors` /
 *  `bezierMemory` are index-parallel to the points, so a cut that renumbers the
 *  anchors would leave every lock and bezier snapshot pointing at the wrong one
 *  — the same reason construction's segment delete drops them. `rawPoints` /
 *  `smoothness` are the freehand source the visible geometry is re-derived
 *  from, and re-deriving would undo the cut; they're documented as dropped by
 *  the first destructive edit for exactly that reason. The rotation frame goes
 *  for the same reason a resize clears it: it describes an un-rotated shape
 *  that no longer exists. */
function reshapedSeam(seam: StitchLine): StitchLine {
  const {
    rawPoints: _rawPoints,
    smoothness: _smoothness,
    smoothAnchors: _smoothAnchors,
    bezierMemory: _bezierMemory,
    rotation: _rotation,
    rotationCenter: _rotationCenter,
    pivotPoint: _pivotPoint,
    authoredPoints: _authoredPoints,
    authoredClosed: _authoredClosed,
    authoredHandles: _authoredHandles,
    ...rest
  } = seam;
  return rest;
}

export class LeatherDeleteController {
  /** Ephemeral outline drawn over the object a click would remove. */
  private highlight: paper.Item | null = null;
  private hovered: LeatherPick = null;
  /** The run of the hovered seam a tap would take, when it takes only one. */
  private hoveredRun: { index: number; pathData: string } | null = null;

  constructor(private host: LeatherDeleteHost) {}

  /** Test hook (#1000): what the next click would delete, as `"<kind>:<id>"`
   *  — or `"stitch:<id>#<run>"` when only one run of a seam would go (#1030).
   *  Null when the cursor is over bare mat. */
  getHoverTarget(): string | null {
    const h = this.hovered;
    if (!h) return null;
    if (h.kind === "piece") return `piece:${h.pieceId}`;
    if (h.kind === "cutout") return `cutout:${h.cutoutId}`;
    return this.hoveredRun
      ? `stitch:${h.stitchId}#${this.hoveredRun.index}`
      : `stitch:${h.stitchId}`;
  }

  handleHover(p: { x: number; y: number }, shiftKey = false): void {
    this.refresh(p, shiftKey);
  }

  /**
   * Delete what's under the cursor — whatever the highlight is tracing.
   *
   * A whole object selects first and then deletes the selection, deliberately:
   * it reuses the one code path the keyboard and the trashcan already use, so
   * cascade / pruning / undo can't diverge between the three ways of removing
   * the same object. A seam run has no such shared path, so it commits here.
   */
  handleDown(p: { x: number; y: number }, shiftKey = false): boolean {
    this.refresh(p, shiftKey);
    const pick = this.hovered;
    if (!pick) return false;
    if (pick.kind === "stitch" && this.hoveredRun) {
      const done = this.cutSeamRun(
        pick.pieceId,
        pick.stitchId,
        this.hoveredRun.index,
      );
      this.clear();
      return done;
    }
    this.host.setSelection(selectionForPick(pick));
    this.host.deleteSelection();
    this.clear();
    return true;
  }

  /** Drop the highlight. Called on tool switch / cancel / after a delete. */
  clear(): void {
    if (this.highlight) {
      this.highlight.remove();
      this.highlight = null;
    }
    this.hovered = null;
    this.hoveredRun = null;
  }

  // -------------------------------------------------------------------
  // Hover highlight
  // -------------------------------------------------------------------

  /**
   * Outline what a click would remove. A destructive one-click tool without
   * this is a trap: the hit target for a seam or a cutout isn't obvious, and
   * "which of these did I just delete?" is a bad thing to learn from an undo.
   */
  private refresh(cursor: { x: number; y: number }, shiftKey: boolean): void {
    const scene = this.host.getScene();
    const scope = this.host.getScope();
    const project = this.host.getProject();
    const pick = pickLeatherObjectAt(scene, project, scope, cursor, {
      includeStitch: true,
    });
    const run =
      pick && pick.kind === "stitch" && !shiftKey
        ? this.runUnder(pick.pieceId, pick.stitchId, cursor)
        : null;
    const key = (
      p: LeatherPick,
      r: { index: number } | null,
    ): string | null => {
      if (!p) return null;
      if (p.kind === "piece") return `piece:${p.pieceId}`;
      if (p.kind === "cutout") return `cutout:${p.cutoutId}`;
      return r ? `stitch:${p.stitchId}#${r.index}` : `stitch:${p.stitchId}`;
    };
    if (key(pick, run) === this.getHoverTarget()) return;
    this.clear();
    this.hovered = pick;
    this.hoveredRun = run;
    if (!pick) return;

    const d = run ? run.pathData : this.outlineFor(pick);
    if (!d) return;
    try {
      const item = new scope.CompoundPath({ pathData: d, insert: true });
      // A SEAM's highlight lands exactly on the seam, so an accent outline
      // reads as no change at all — the object is already a dark line under
      // dark chisel marks. It gets construction's delete-hover treatment
      // instead (red, dashed), which is both legible over leather and the
      // look this app already uses for "this stretch is about to go". A part
      // or cut-out keeps the accent: its outline traces the boundary between
      // leather and mat, where the accent reads perfectly well.
      const onSeam = pick.kind === "stitch";
      item.strokeColor = new scope.Color(
        onSeam
          ? DELETE_HOVER()
          : scene.isPointOverLeather(cursor)
            ? ACCENT_OVER_LEATHER()
            : ACCENT(),
      );
      item.strokeWidth = onSeam ? 2.6 : 2.5;
      if (onSeam) item.dashArray = [6, 4];
      item.strokeScaling = false;
      item.fillColor = null;
      item.data = { ephemeral: true };
      this.highlight = item;
    } catch {
      /* highlight only — an unparseable path just doesn't draw */
    }
  }

  /** The run of a seam under the cursor, in DISPLAY space, or null when this
   *  seam comes off whole. A seam that is re-derived on every commit is not
   *  cuttable a run at a time: a traced one (`constructionOutline`) rebuilds
   *  from its construction lines — that edit belongs on the lines — and a
   *  cropped one (`authoredPoints`) re-crops from geometry the cut never
   *  touched, so either way the next commit would put the run straight back. */
  private runUnder(
    pieceId: string,
    stitchId: string,
    cursor: { x: number; y: number },
  ): { index: number; pathData: string } | null {
    const project = this.host.getProject();
    const scope = this.host.getScope();
    const piece = project.pieces.find((p) => p.id === pieceId);
    if (!piece || !isVisible(piece)) return null;
    const seam = pieceStitchLines(piece).find((s) => s.id === stitchId);
    if (!seam || seam.constructionOutline || seam.authoredPoints) return null;
    const off = pieceDisplayOffset(
      piece,
      this.host.getScene().getPartLayoutView(),
    );
    const run = nearestSeamRun(
      seam,
      { x: cursor.x - off.dx, y: cursor.y - off.dy },
      scope,
      RUN_PICK_TOL_MM,
    );
    if (!run) return null;
    return {
      index: run.index,
      pathData:
        off.dx || off.dy
          ? translatePathData(run.pathData, off.dx, off.dy, scope)
          : run.pathData,
    };
  }

  /** Cut one run out of a seam. Returns false if the seam has gone missing
   *  under the cursor between the hover and the tap. */
  private cutSeamRun(
    pieceId: string,
    stitchId: string,
    index: number,
  ): boolean {
    const project = this.host.getProject();
    const piece = project.pieces.find((p) => p.id === pieceId);
    const seam = piece
      ? pieceStitchLines(piece).find((s) => s.id === stitchId)
      : undefined;
    if (!piece || !seam) return false;
    const remains = seamsAfterRunDelete(seam, index);
    if (remains.length === 0) {
      // The run WAS the seam. Route the removal through the shared delete so
      // cascade, pruning and undo match every other way of losing a seam.
      this.host.setSelection(
        selectionForPick({ kind: "stitch", pieceId, stitchId }),
      );
      this.host.deleteSelection();
      return true;
    }
    this.host.commit("Delete seam segment", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((p) => {
        if (p.id !== pieceId) return p;
        const next: StitchLine[] = [];
        for (const s of pieceStitchLines(p)) {
          if (s.id !== stitchId) {
            next.push(s);
            continue;
          }
          remains.forEach((r, k) => {
            const base = reshapedSeam(s);
            next.push(
              k === 0
                ? { ...base, ...r }
                : {
                    // A middle cut leaves two seams. The second is a new
                    // object: its own id and name, and none of the derived
                    // relationships (#199 match / grid) that belong to the
                    // seam that kept the id.
                    ...base,
                    ...r,
                    id: newId("stitch"),
                    name: nextStitchName(proj, k - 1),
                    matchId: undefined,
                    holeCount: undefined,
                    gridPhase: undefined,
                    gridHoles: undefined,
                    gridCornerHoles: undefined,
                    gridMain: undefined,
                  },
            );
          });
        }
        return { ...p, stitchLines: next };
      }),
    }));
    return true;
  }

  /** The display-space outline of a picked object, for the highlight. */
  private outlineFor(pick: NonNullable<LeatherPick>): string | null {
    const project = this.host.getProject();
    const scope = this.host.getScope();
    const view = this.host.getScene().getPartLayoutView();
    const piece = project.pieces.find((p) => p.id === pick.pieceId);
    if (!piece || !isVisible(piece)) return null;
    const off = pieceDisplayOffset(piece, view);
    const shift = (d: string) =>
      off.dx || off.dy ? translatePathData(d, off.dx, off.dy, scope) : d;
    if (pick.kind === "piece") return shift(piece.shapePathData);
    if (pick.kind === "cutout") {
      const cut = (piece.cutouts ?? []).find((c) => c.id === pick.cutoutId);
      return cut ? shift(cut.pathData) : null;
    }
    const seam = pieceStitchLines(piece).find((s) => s.id === pick.stitchId);
    if (!seam || seam.points.length < 2) return null;
    // `seamPathData` closes a CLOSED seam itself. Without the Z the run between
    // the last point and the first goes unmarked — on an offset seam ringing a
    // part that reads as one segment mysteriously not highlighting — and it
    // carries the seam's curves (#982), so the highlight traces what's drawn.
    const d = seamPathData(seam);
    return d ? shift(d) : null;
  }
}
